import { PDFViewer, ToggleText } from "@/components";
import Popover from "@/components/Popover";
import { SkeletonChatDetail } from "@/components/Skeletons";
import { useGetChat } from "@/hooks/chat/useGetChat";
import { useAuthStore } from "@/store/authStore";
import useChatStore from "@/store/chatStore";
import { CHAT_ROLE, MimeType } from "@/types";
import {
  checkImageFromMimeType,
  downloadFile,
  getFileNameFromPath,
  getMimeTypeFromUrl,
} from "@/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BsCloudDownloadFill } from "react-icons/bs";
import { IoIosImages } from "react-icons/io";
import { MdMoreVert } from "react-icons/md";
import ChangeRoomOwnerDialog from "../components/ChangeRoomOwnerDialog";
import DocumentPreviewModal, {
  type PreviewAttachment,
} from "../components/DocumentPreviewModal";
import RoomEditDrawer from "./RoomEdit/RoomEditDrawer";

interface Props {
  roomId: number;
  showUser: () => void;
  showFile: () => void;
}

const RoomInfomation: React.FC<Props> = ({ roomId, showFile, showUser }) => {
  // Chat global state to manage all chat functions
  const { memberNumber, setChatRole, setMemberNumber, setAttachment } =
    useChatStore();

  const { user } = useAuthStore();

  // State control drawer edit room
  const [open, setOpen] = useState(false);

  // State control change owner modal
  const [openChangeOwner, setOpenChangeOwner] = useState(false);

  // Preview PDF / document from room thumbnail
  const [previewAttachment, setPreviewAttachment] =
    useState<PreviewAttachment | null>(null);

  const { chat, isLoading } = useGetChat(roomId);

  const roomFile = useMemo(() => {
    if (!chat?.image) return null;

    const url = chat.image;
    const name = getFileNameFromPath(url);
    const type = getMimeTypeFromUrl(url);
    const isImage = checkImageFromMimeType(type);
    const isPdf = type === MimeType.PDF;

    return { url, name, type, isImage, isPdf };
  }, [chat?.image]);

  // Get chat role of user
  const getChatRole = useCallback(() => {
    return chat?.created_by?.id === user?.id
      ? CHAT_ROLE.INCHARGE
      : CHAT_ROLE.MEMBER;
  }, [chat, user]);

  // Set chat role member or incharge
  useEffect(() => {
    if (chat && user) {
      setChatRole(getChatRole());
    }

    // set number of members in chat
    if (chat) {
      setMemberNumber(chat.chatUsers.length || 0);
    }
  }, [chat, user]);

  // open drawer edit room
  const handleOpenEditDrawer = () => {
    setOpen(true);
  };

  // open change owner modal
  const handleOpenChangeOwner = () => {
    setOpenChangeOwner(true);
  };

  // Download file
  const handleDownload = useCallback((url: string, name: string) => {
    downloadFile(url, name);
  }, []);

  const handlePreviewRoomFile = useCallback(() => {
    if (!roomFile) return;

    if (roomFile.isImage) {
      setAttachment({
        attachmentList: [
          { url: roomFile.url, type: roomFile.type, name: roomFile.name },
        ],
        currentIndex: 0,
      });
      return;
    }

    if (roomFile.isPdf) {
      setPreviewAttachment({
        url: roomFile.url,
        type: roomFile.type,
        name: roomFile.name,
      });
    }
  }, [roomFile, setAttachment]);

  if (isLoading) return <SkeletonChatDetail />;

  const chatOwner = chat?.created_by;

  return (
    <div className="flex gap-4 p-4 ml-12 lg:ml-0">
      {/* Chat thumbnail */}
      {roomFile ? (
        <div className="relative group hidden md:block size-[140px] 2xl:size-[180px] shrink-0">
          <button
            type="button"
            className="bg-cyan text-light_default absolute top-2 right-2 z-10 p-1 rounded-full invisible group-hover:visible"
            aria-label="ダウンロード"
            title="ダウンロード"
            onClick={(event) => {
              event.stopPropagation();
              handleDownload(roomFile.url, roomFile.name);
            }}
          >
            <BsCloudDownloadFill />
          </button>

          {roomFile.isPdf ? (
            <button
              type="button"
              className="relative flex justify-center items-center bg-grey-200 rounded-lg size-full overflow-hidden cursor-pointer"
              aria-label="プレビュー"
              onClick={handlePreviewRoomFile}
            >
              <PDFViewer
                file={roomFile.url}
                width="100%"
                height="100%"
                page={1}
                toolbar={0}
                navpanes={0}
                scrollbar={0}
                className="absolute inset-0 pointer-events-none"
              />
            </button>
          ) : (
            <img
              src={roomFile.url}
              alt="Chat Thumbnail"
              className="aspect-square object-cover rounded-lg size-full cursor-pointer"
              onClick={handlePreviewRoomFile}
            />
          )}
        </div>
      ) : (
        <div className="justify-center items-center bg-grey-200 rounded-lg hidden md:flex size-[140px] 2xl:size-[180px]">
          <IoIosImages className="size-32" />
        </div>
      )}

      <div className="space-y-2 flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-6">
          {/* Title chat  */}
          <h1 className="font-semibold text-light_primary line-clamp-2 wrap-break-word">
            {chat?.title}
          </h1>

          {/* Popover menu edit room */}
          {/* Only show edit options for incharge role (creator)  */}
          {!!chat && getChatRole() === CHAT_ROLE.INCHARGE && (
            <Popover
              trigger={
                <button
                  type="button"
                  className="flex items-center justify-center shrink-0"
                >
                  <MdMoreVert className="size-6 hover:text-cyan" />
                </button>
              }
              side="bottom"
              align="end"
              asChild
            >
              <div className="flex flex-col bg-dark_primary rounded-lg shadow-light_dropdown border-grey-200 text-sm text-light_primary ">
                <button
                  type="button"
                  onClick={handleOpenEditDrawer}
                  className="py-2 px-4 hover:bg-grey-200 transition-colors duration-100 text-left"
                >
                  情報編集
                </button>

                <button
                  type="button"
                  onClick={showUser}
                  className="py-2 px-4 hover:bg-grey-200 transition-colors duration-100 text-left xl:hidden"
                >
                  {`ルーム（${memberNumber}）`}
                </button>

                <button
                  type="button"
                  onClick={showFile}
                  className="py-2 px-4 hover:bg-grey-200 transition-colors duration-100 text-left xl:hidden"
                >
                  添付ファイル
                </button>

                <button
                  type="button"
                  onClick={handleOpenChangeOwner}
                  className="py-2 px-4 hover:bg-grey-200 text-error_main transition-colors duration-100 text-left"
                >
                  チャットを退出
                </button>
              </div>
            </Popover>
          )}
        </div>

        <div className="text-xs text-light_secondary flex items-center">
          <div className="flex gap-2 max-w-[50%]">
            <span className="shrink-0">ユーザー</span>
            <span
              className="text-cyan line-clamp-1 wrap-break-word"
              title={chatOwner?.full_name || chatOwner?.email}
            >
              {chatOwner?.full_name || chatOwner?.email || "退職者"}
            </span>
          </div>
          {!!chatOwner?.company && (
            <div className="flex gap-2 max-w-[50%]">
              <span className="ml-4 shrink-0">会社</span>
              <span
                className="text-cyan line-clamp-1 wrap-break-word"
                title={chatOwner?.company?.name}
              >
                {chatOwner?.company?.name}
              </span>
            </div>
          )}
        </div>

        {/* Description chat  */}
        {!!chat?.description && (
          <ToggleText
            text={chat.description}
            className="max-h-32 overflow-y-auto"
          />
        )}
      </div>
      <RoomEditDrawer open={open} setOpen={setOpen} roomId={roomId} />
      {openChangeOwner && chat && (
        <ChangeRoomOwnerDialog
          open={openChangeOwner}
          setOpen={setOpenChangeOwner}
          roomId={roomId}
          currentOwner={chatOwner}
        />
      )}

      <DocumentPreviewModal
        open={!!previewAttachment}
        onOpenChange={(open) => {
          if (!open) setPreviewAttachment(null);
        }}
        attachment={previewAttachment}
      />
    </div>
  );
};

export default RoomInfomation;
